import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Form, Input } from "semantic-ui-react";
import Report from "../../../models/Report";
import DataTable from "../dataTable";

const initialTable = {
    page: 1,
    term: "",
    sort: "percentageBelowThreshold",
    order: "asc",
    limit: 25,
    threshold: 1,
};

export default function EntityBookingActivityThreshold() {
    const [loading, setLoading] = useState(true);
    const [table, setTable] = useState(initialTable);
    const [threshold, setThreshold] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [data, setData] = useState([]);

    const getData = useCallback(async (params) => {
        const nextParams = {
            ...params,
            threshold: Math.max(parseInt(params.threshold || 1, 10) || 1, 1),
        };
        setLoading(true);
        const response = await Report.entityBookingActivityThreshold(nextParams);
        setData(response.items);
        setTotalRecords(response.total);
        setTable(nextParams);
        setThreshold(nextParams.threshold);
        setLoading(false);
    }, []);

    useEffect(() => {
        getData(initialTable);
    }, [getData]);

    const onThresholdChange = ({ target }) => {
        const nextThreshold = Math.max(
            parseInt(target.value || 1, 10) || 1,
            1,
        );
        getData({
            ...table,
            page: 1,
            threshold: nextThreshold,
        });
    };

    const headers = [
        { key: "entity", text: "Entity" },
        { key: "totalMembers", text: "Total members" },
        { key: "membersBelowThreshold", text: "Members below threshold" },
        { key: "percentageBelowThreshold", text: "% below threshold" },
        {
            key: "membersAtOrAboveThreshold",
            text: "Members at/above threshold",
        },
        { key: "percentageAtOrAboveThreshold", text: "% at/above threshold" },
    ];
    const tableData = data.map((item) => {
        const percentageBelowThreshold = Number(
            item.percentageBelowThreshold || 0,
        );
        const percentageAtOrAboveThreshold = Number(
            item.percentageAtOrAboveThreshold || 0,
        );
        return {
            entity: {
                value: <Link to={`/entities/${item._id}`}>{item.name}</Link>,
                search: item.name,
            },
            totalMembers: {
                value: item.totalMembers,
                search: item.totalMembers,
            },
            membersBelowThreshold: {
                value: item.membersBelowThreshold,
                search: item.membersBelowThreshold,
            },
            percentageBelowThreshold: {
                value: `${percentageBelowThreshold.toFixed(2)}%`,
                search: percentageBelowThreshold,
            },
            membersAtOrAboveThreshold: {
                value: item.membersAtOrAboveThreshold,
                search: item.membersAtOrAboveThreshold,
            },
            percentageAtOrAboveThreshold: {
                value: `${percentageAtOrAboveThreshold.toFixed(2)}%`,
                search: percentageAtOrAboveThreshold,
            },
        };
    });

    return (
        <>
            <h4>Entity booking activity threshold</h4>
            <p>
                The percentage of each entity's current members whose all-time
                Tee Time and Event booking activity is below or at/above the
                selected threshold.
            </p>
            <Form className='mb-3'>
                <Form.Field width={4}>
                    <label>Booking threshold</label>
                    <Input
                        min={1}
                        type='number'
                        value={threshold}
                        onChange={onThresholdChange}
                    />
                </Form.Field>
            </Form>
            <DataTable
                loading={loading}
                headers={headers}
                data={tableData}
                totalRecords={totalRecords}
                page={table.page}
                onChange={getData}
                searchTerm={table.term}
                sort={table.sort}
                order={table.order}
                limit={table.limit}
                sortOptions={[
                    {
                        text: "% below threshold",
                        key: "percentageBelowThreshold",
                    },
                    {
                        text: "% at/above threshold",
                        key: "percentageAtOrAboveThreshold",
                    },
                    { text: "Entity", key: "name" },
                    { text: "Total members", key: "totalMembers" },
                    {
                        text: "Members below threshold",
                        key: "membersBelowThreshold",
                    },
                    {
                        text: "Members at/above threshold",
                        key: "membersAtOrAboveThreshold",
                    },
                ]}
                pagination
            />
        </>
    );
}
